import axios from 'axios';
import * as httpStatusCodes from '@/constants/httpStatusCodes';

let instance = null;

let restoreRegisterCount = 0;
const MAX_RESTORE_COUNT = 5;

let networkErrorsCount = 0;
const MAX_NETWORK_ERRORS_COUNT = 5;

const SESSION_ID_KEY = 'session-id';
const USERNAME = 'username';

const LOGOUT_REASONS = {
  SERVICE_UNAVAILABLE: '500 Service Unavailable. Please try again later.',
  UNAUTHORIZED: '401 Unauthorized. eAgent automated log out.',
  LICENSE_LIMIT: 'License Limit Reached. eAgent automated log out.',
  MAX_401_KEEP_ALIVE_FAILED: `401 - Max consecutive keepAlive failed requests (${MAX_RESTORE_COUNT}). eAgent automated log out.`,
  MAX_500_KEEP_ALIVE_FAILED: `500 - Max consecutive keepAlive failed requests (${MAX_RESTORE_COUNT}). eAgent automated log out.`,
  NETWORK_ERROR: `Network Error. Max consecutive network errors reached (${MAX_NETWORK_ERRORS_COUNT}). eAgent automated log out.`,
  SESSION_EXPIRED: 'Session Expired'
};

const SERVER_ERROR_MESSAGES = {
  LICENSE_LIMIT: 'Your Organization has reached its Agent license count and you are unable to login',
  FAILED_TO_RETRIEVE_ORG_CONFIG: 'Failed to retrieve latest ORG configuration',
  NO_REGISTERED_SESSION_FOUND: 'No registered session found',
  AGENT_CANNOT_DIAL: 'Agent Cannot Dial',
  WRONG_PARAMETER: 'Wrong Parameter',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  REGISTRATION_FAILED: 'Registration Failed'
};

const API = {
  LOGIN: 'api/login',
  KEEP_ALIVE: 'api/agent/current/keepAlive',
  REGISTER: 'api/agent/register'
};

const interceptors = {
  request: {
    onFulfilled(config) {
      if (!config.headers[SESSION_ID_KEY]) {
        HttpRequestHandler.setSessionId(sessionStorage.getItem('sessionId'));
        config.headers[SESSION_ID_KEY] = sessionStorage.getItem('sessionId');
      }
      return config;
    }
  },
  response: {
    onFulfilled(response) {
      const isKeepAlive = response.config.url === API.KEEP_ALIVE;
      if (isKeepAlive && restoreRegisterCount > 0) {
        this._reRegister();
      }

      const isBlob = response.data instanceof Blob;
      if (response && response.data && !isBlob && !response.data.data) {
        // ERR_INCOMPLETE_CHUNKED_ENCODING
        // ERR_CONNECTION_REFUSED
        // ERR_EMPTY_RESPONSE
        return Promise.reject(new Error('200 Status but no data found'));
      }

      return response && response.data;
    },
    onRejected(error) {
      if (error && error.message === 'Network Error' && ++networkErrorsCount >= MAX_NETWORK_ERRORS_COUNT) {
        return this._logout({ reason: LOGOUT_REASONS.NETWORK_ERROR });
      }

      if (error && error.config && !error.response) {
        return this.handleConnectionError({ message: error.message, code: error.code });
      }

      const isLogin = error.response.config.url === API.LOGIN;
      const isKeepAlive = error.response.config.url === API.KEEP_ALIVE;
      const isRegister = error.response.config.url === API.REGISTER;
      const isInternalServerError = error.response.status === httpStatusCodes.INTERNAL_SERVER_ERROR;
      const isUnauthorized = error.response.status === httpStatusCodes.UNAUTHORIZED;
      const isNotFound = error.response.status === httpStatusCodes.NOT_FOUND;
      const message = (error.response.data && error.response.data.error && error.response.data.error.message) || '';

      if (isInternalServerError) {
        if (message.includes(SERVER_ERROR_MESSAGES.LICENSE_LIMIT)) {
          return this._logout({
            reason: LOGOUT_REASONS.LICENSE_LIMIT,
            isLicenseLimit: true
          });
        }

        if (message.includes(SERVER_ERROR_MESSAGES.FAILED_TO_RETRIEVE_ORG_CONFIG)) {
          return this._logout({
            reason: LOGOUT_REASONS.SERVICE_UNAVAILABLE,
            isServiceUnavailable: true
          });
        }

        if (message.includes(SERVER_ERROR_MESSAGES.SESSION_EXPIRED)) {
          const runningOnTeams = localStorage.getItem('runningOnTeams');
          if (runningOnTeams) {
            const orgData = JSON.parse(localStorage.getItem('organizationData') || {});
            const { orgName, shortName } = orgData;
            setTimeout(() => {
              window.location.href = `${window.location.origin}/teams?org=${shortName || orgName}`;
            }, 5000);
          }
          return this._logout({ reason: LOGOUT_REASONS.SESSION_EXPIRED, isSessionExpired: true, runningOnTeams });
        }

        if (message.includes(SERVER_ERROR_MESSAGES.WRONG_PARAMETER)) {
          return Promise.reject(error);
        }

        if (isRegister) {
          let message = error.response.data
            ? error.response.data.error
              ? error.response.data.error.message
              : SERVER_ERROR_MESSAGES.REGISTRATION_FAILED
            : SERVER_ERROR_MESSAGES.REGISTRATION_FAILED;

          if (message.includes('Failed with status 400')) {
            message = /(?:')(.*)(?:')/.exec(message)[1];
          }

          return Promise.reject(message);
        }

        if (!message.includes('Try Later')) {
          return this.handle500Error({ isKeepAlive });
        }

        return Promise.reject(error);
      }

      if (isUnauthorized) {
        if (message.includes('Illegal Session Id')) {
          return this._logout({ reason: `401 Unauthorized. ${error.response.data.error.message}` });
        } else {
          return this.handle40xError({ isLogin, isKeepAlive });
        }
      }

      if (isNotFound && isKeepAlive) {
        return this._reRegister();
      }

      if (message.includes(SERVER_ERROR_MESSAGES.NO_REGISTERED_SESSION_FOUND)) {
        this._reRegister();
      }

      if (message.includes(SERVER_ERROR_MESSAGES.AGENT_CANNOT_DIAL)) {
        // this._showError(error.response);
      }

      return Promise.reject(error);
    }
  }
};

export default class HttpRequestHandler {
  constructor({ hostName = null }) {
    if (instance) {
      return instance;
    }

    instance = axios.create({
      baseURL: hostName || window.location.origin,
      headers: {
        // 'Content-Type': 'application/json'
        // 'Content-Encoding': 'gzip'
      },
      timeout: 30000
    });

    instance.interceptors.request.use(interceptors.request.onFulfilled.bind(this), null, { synchronous: true });
    instance.interceptors.response.use(
      interceptors.response.onFulfilled.bind(this),
      interceptors.response.onRejected.bind(this)
    );

    return instance;
  }

  static setSessionId(sessionId) {
    if (sessionId) {
      instance.defaults.headers[SESSION_ID_KEY] = sessionId;
    }
  }

  static setSessionUsername(username) {
    if (username) {
      instance.defaults.headers[USERNAME] = username;
    }
  }

  static removeSessionId() {
    sessionStorage.removeItem('sessionId');
  }

  handle40xError({ isLogin, isKeepAlive }) {
    if (!isLogin && !isKeepAlive) {
      this._logout({ reason: LOGOUT_REASONS.UNAUTHORIZED, isUnauthorized: true });
    } else if (isKeepAlive && ++restoreRegisterCount >= MAX_RESTORE_COUNT) {
      this._logout({ reason: LOGOUT_REASONS.MAX_401_KEEP_ALIVE_FAILED, isUnauthorized: true });
    }
  }

  handle500Error({ isKeepAlive }) {
    if (isKeepAlive && ++restoreRegisterCount >= MAX_RESTORE_COUNT) {
      this._logout({ reason: LOGOUT_REASONS.MAX_500_KEEP_ALIVE_FAILED });
    }
  }

  handleConnectionError(details) {
    console.log('http errors.', details);
  }

  _getVueInstance() {
    return document.querySelector('#app').__vue__;
  }

  _showError(error) {
    this._getVueInstance().$root.$emit('ecs-error', error);
  }

  _reRegister() {
    restoreRegisterCount = 0;
    networkErrorsCount = 0;
    this._getVueInstance().$root.$emit('re-register', { showConnectionRestoredMessage: true });
  }

  _logout(info) {
    this._getVueInstance().$root.$emit('log-out', info);
  }
}
